/**
 * FEAT-096 / S-404 — the sources editor, as data.
 *
 * SourceDraft holds every field as a plain string (what the inputs produce);
 * a type-specific field left blank for a type that doesn't need it is
 * dropped, not sent as an empty string. Validated client-side so a founder
 * catches a missing sitemap_url before the round trip, but the API
 * (newsletters.ts's sourceSchema) re-validates independently — this is a
 * convenience, not the source of truth.
 */

import type { NewsletterSource, NewsletterSourceType } from "@/types/newsletter";

export interface SourceDraft {
  name: string;
  url: string;
  type: NewsletterSourceType;
  rss_feed: string;
  sitemap_url: string;
  url_pattern: string;
  channel_id: string;
}

export function emptySourceDraft(): SourceDraft {
  return { name: "", url: "", type: "blog", rss_feed: "", sitemap_url: "", url_pattern: "", channel_id: "" };
}

export function sourceToDraft(s: NewsletterSource): SourceDraft {
  return {
    name: s.name,
    url: s.url,
    type: s.type,
    rss_feed: s.rss_feed ?? "",
    sitemap_url: s.sitemap_url ?? "",
    url_pattern: s.url_pattern ?? "",
    channel_id: s.channel_id ?? "",
  };
}

export type BuildSourcesResult =
  | { ok: true; sources: NewsletterSource[] }
  | { ok: false; errors: string[] };

const TYPE_LABEL: Record<NewsletterSourceType, string> = {
  blog: "Blog",
  sitemap: "Sitemap",
  podcast: "Podcast",
  youtube: "YouTube",
};

export function buildSourcesPatch(drafts: SourceDraft[]): BuildSourcesResult {
  const errors: string[] = [];
  const seen = new Map<string, number>();
  const sources: NewsletterSource[] = [];

  drafts.forEach((d, i) => {
    const row = `Row ${i + 1}`;
    if (!d.name.trim()) errors.push(`${row}: name is required.`);
    if (!d.url.trim()) errors.push(`${row}: URL is required.`);
    if (d.type === "sitemap" && !d.sitemap_url.trim()) {
      errors.push(`${row} (${TYPE_LABEL.sitemap}): Sitemap URL is required.`);
    }
    if (d.type === "youtube" && !d.channel_id.trim()) {
      errors.push(`${row} (${TYPE_LABEL.youtube}): Channel ID is required.`);
    }
    if (d.name.trim()) {
      const first = seen.get(d.name);
      if (first !== undefined) {
        errors.push(`${row}: duplicate name "${d.name}" (already used in row ${first + 1}).`);
      } else {
        seen.set(d.name, i);
      }
    }

    sources.push({
      name: d.name,
      url: d.url,
      type: d.type,
      rss_feed: d.type === "blog" || d.type === "podcast" ? d.rss_feed.trim() || null : null,
      sitemap_url: d.type === "sitemap" ? d.sitemap_url.trim() || null : null,
      url_pattern: d.type === "sitemap" ? d.url_pattern.trim() || null : null,
      channel_id: d.type === "youtube" ? d.channel_id.trim() || null : null,
    });
  });

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, sources };
}
